import React from 'react'
import { Typography, Container } from '@cleartrip/ct-design-components'
import { FilterData as FilterDataType, PossibleFilterTypes } from '@/types/srp'
import { filterTypes } from '@/constants/Srp'
import { formatCurrency } from '@/utils'
import { useAppSelector } from '@/redux/store/hooks'
import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { isValidPrice } from '@/utils/general'

type PossibleStopValues = 0 | 1 | 2

type StopsFilterProps = {
  stops: {
    options: PossibleStopValues[]
    selectedValues: PossibleStopValues[]
    prices?: Partial<Record<PossibleStopValues, number>>
    pricesADT?: Partial<Record<PossibleStopValues, number>>
  }
  onFilterChange: (type: PossibleFilterTypes, filtersData: FilterDataType) => void
  isPerAdultPricing?: boolean
  from?: string
  isRT?: boolean
  isRevamp?: boolean
  route?: string
}

const stopsObject: Record<PossibleStopValues, string> = {
  0: 'Non Stop',
  1: '1 Stop',
  2: '2 & more',
}
const REVAMP_TILE_SELECTED = { boxShadow: 'inset 0 0 0 0.5px #1a1a1a' }
const REVAMP_TILE_BASE_CLASS = 'flex flex-1 flex-column flex-middle bs-border br-12 ba-solid px-2 py-3 c-pointer'
const REVAMP_TILE_CLASS = `${REVAMP_TILE_BASE_CLASS} bc-neutral-500 bg-neutral-100`
const REVAMP_TILE_CLASS_SELECTED = `${REVAMP_TILE_BASE_CLASS} bc-neutral-900 bg-neutral-300`

const StopsFilter = ({
  stops,
  onFilterChange,
  isPerAdultPricing = true,
  from,
  isRT,
  isRevamp = false,
  route = '',
}: StopsFilterProps) => {
  const { options, selectedValues = [], prices = {}, pricesADT = {} } = stops || {}
  const { searchData } = useAppSelector(getSrpSearchData)
  const airportDetail = searchData?.metaData?.airportDetail || {}
  const fromCity = (from && airportDetail[from]?.city) || ''

  const getPriceForStop = (index: number) => {
    const priceData = isPerAdultPricing ? pricesADT : prices
    const price = priceData[index as keyof typeof priceData]
    return isValidPrice(price) ? formatCurrency(Number(price)) : null
  }

  const onStopClick = (option: PossibleStopValues) => {
    let updatedStopsSelectedValues
    if (selectedValues.includes(option)) {
      updatedStopsSelectedValues = selectedValues.filter(value => value !== option)
    } else {
      updatedStopsSelectedValues = [...selectedValues, option]
    }
    onFilterChange(filterTypes.STOPS, updatedStopsSelectedValues)
  }

  if (isRevamp) {
    return (
      <Container className='flex flex-column'>
        {route && (
          <Typography className='mb-1' color='subheading' variant='P3'>
            {route}
          </Typography>
        )}
        <Container className='flex flex-cg-2'>
          {options.map((option, index) => {
            const checked = selectedValues.includes(option)
            const price = getPriceForStop(index)
            const tileClassName = checked ? REVAMP_TILE_CLASS_SELECTED : REVAMP_TILE_CLASS
            const priceText = price ? `@${price}` : ' '

            return (
              <Container
                key={option}
                className={tileClassName}
                css={checked ? REVAMP_TILE_SELECTED : undefined}
                onClick={() => onStopClick(option)}
              >
                <Typography color='heading' variant='B2'>
                  {stopsObject[option]}
                </Typography>
                <Typography color='subheading' variant='P3'>
                  {priceText}
                </Typography>
              </Container>
            )
          })}
        </Container>
      </Container>
    )
  }

  return (
    <>
      {isRT && <Typography variant='B2' className='c-pointer py-3'>{`Stops from ${fromCity}`}</Typography>}
      <Container className='flex'>
        {options.map((option, index) => {
          const checked = selectedValues.includes(option)
          const price = getPriceForStop(index)
          const isFirst = index === 0
          const isLast = index === options.length - 1
          const isSingleOption = options.length === 1

          return (
            <Container
              key={option}
              className='flex flex-column flex-center p-3 c-pointer mb-3'
              css={{
                border: checked ? '1px solid #000' : '1px solid #E7E7E7',
                backgroundColor: checked ? '#F3F3F3' : '#fff',
                width: '86px',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRadius: isSingleOption ? '8px' : isFirst ? '8px 0 0 8px' : isLast ? '0 8px 8px 0' : '0',
                marginLeft: !isFirst ? '-1px' : '0',
                zIndex: checked ? 2 : 1,
                position: 'relative',
              }}
              onClick={() => onStopClick(option)}
            >
              <Typography
                variant={'P3'}
                css={{
                  color: '#000',
                  textAlign: 'center',
                  marginBottom: '4px',
                  fontWeight: checked ? '600' : '500',
                }}
              >
                {stopsObject[option]}
              </Typography>
              <Typography variant='P3' css={{ color: '#666', textAlign: 'center' }}>
                {price}
              </Typography>
            </Container>
          )
        })}
      </Container>
    </>
  )
}

export default StopsFilter
